import React from 'react'
import { Box, Text } from '@common/fundamentals'
import { Button, IconButton, LoadingButton } from '@common/material'
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { localizedStrings } from '@core/strings'
import { useBypassDocumentReview } from '@core/react-query/features/siteBoxDocuments'
import { useToast } from '@core/toast'
import { ModalDialogContainer } from '@common/modal'
import { DatePicker } from '@common/material/DatePicker'
import moment from 'moment'
import { Controller, useForm } from 'react-hook-form'
import { DocumentIntelligence } from '@core/entities/SiteBoxDocument'
import { AppraisalStatusButtons } from '../components'
import { SiteBoxDocumentConfigStatus } from '@core/entities/SiteBoxDocumentConfig'

type DocumentAcceptanceStatus = Extract<
  SiteBoxDocumentConfigStatus,
  'PASS' | 'FAIL'
>

type BypassFormValues = {
  isChecked: boolean
  documentAcceptance: DocumentAcceptanceStatus | null
  expiryDate: string | null
}

type BypassModalProps = {
  documentId: number
  allowExpiry?: boolean
  onClose: () => void
  onStartReviewClick: () => void
  onSuccess: () => void
}

export const BypassModal: React.FC<BypassModalProps> = ({
  documentId,
  allowExpiry = false,
  onClose,
  onStartReviewClick,
  onSuccess,
}) => {
  const bypassDocumentReview = useBypassDocumentReview()
  const toast = useToast()

  const { control, handleSubmit, watch, setValue, clearErrors } =
    useForm<BypassFormValues>({
      defaultValues: {
        isChecked: false,
        documentAcceptance: null,
        expiryDate: null,
      },
    })

  const documentAcceptance = watch('documentAcceptance')
  const isExpiryRequired = allowExpiry && documentAcceptance === 'PASS'

  const onSubmit = ({ documentAcceptance, expiryDate }: BypassFormValues) => {
    if (!documentAcceptance) return
    bypassDocumentReview.mutate(
      {
        documentId,
        status: documentAcceptance,
        ...(isExpiryRequired && expiryDate ? { expiryDate } : {}),
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.appraisalSubmitted)
          onSuccess()
        },
        onError: (error) => {
          toast.error(error.message)
        },
      },
    )
  }

  return (
    <ModalDialogContainer onClose={onClose} maxWidth='xs'>
      <DialogTitle
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1 }}
      >
        {localizedStrings.bypassReview}
        <IconButton aria-label='close-modal' onClick={onClose}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Controller
            name='isChecked'
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={
                  <Text fontSize={12} fontWeight={400}>
                    {localizedStrings.documentAlreadyReviewed}
                  </Text>
                }
              />
            )}
          />

          <Text fontSize={12} fontWeight={400} mt={1} mb={2}>
            {localizedStrings.selectBelowToPassOrFail}
          </Text>

          <Controller
            name='documentAcceptance'
            control={control}
            render={({ field }) => (
              <AppraisalStatusButtons
                value={field.value}
                onChange={(status) => {
                  field.onChange(status)
                  clearErrors('expiryDate')
                  if (status !== 'PASS') {
                    setValue('expiryDate', null)
                  }
                }}
                variant='document'
                size='small'
              />
            )}
          />

          {isExpiryRequired && (
            <Box mt={2}>
              <Controller
                name='expiryDate'
                control={control}
                rules={{
                  required: localizedStrings.expiryDateRequired,
                  validate: (date) =>
                    DocumentIntelligence.isExpiryDateInFuture(date) ||
                    localizedStrings.expiryDateMustBeInFuture,
                }}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label={localizedStrings.expiryDate}
                    minDate={moment().add(1, 'day')}
                    value={field.value ? moment(field.value) : null}
                    onChange={(v) =>
                      field.onChange(
                        v ? moment(v).endOf('day').toISOString() : null,
                      )
                    }
                    slotProps={{
                      textField: {
                        required: true,
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button type='button' variant='outlined' onClick={onStartReviewClick}>
            {localizedStrings.startReview}
          </Button>
          <LoadingButton
            type='submit'
            variant='contained'
            loading={bypassDocumentReview.isLoading}
            disabled={!documentAcceptance}
          >
            {localizedStrings.submit}
          </LoadingButton>
        </DialogActions>
      </form>
    </ModalDialogContainer>
  )
}
